import Link from "next/link";
import type { ComponentPropsWithoutRef, ElementType, ReactNode } from "react";

import { statDelta, type StatChange } from "@/lib/office/stats-delta";

import { Badge } from "./Feedback";

/**
 * A grid whose cells are separated by 1px hairlines — the office's table-page
 * toolbars, stats bars, and the reservations calendar are all built from it.
 *
 * The lines are drawn by the container's top + left border plus each cell's
 * right + bottom border (`[&>*]:border-b [&>*]:border-r`), NOT a `bg-line`
 * backdrop showing through `gap-px`. That difference matters: when the final
 * row is incomplete (e.g. 3 stats across 2 columns, or 5 across `lg:grid-cols-5`
 * on a narrow screen), the empty slots are simply absent — transparent — rather
 * than painted line-gray. Cells just supply their own background + padding
 * (e.g. `StatCard`, or a `Panel framed={false}`); no per-cell border needed.
 *
 * Pass the responsive column classes (and any margin) via `className`, e.g.
 * `<HairlineGrid className="grid-cols-2 lg:grid-cols-4">`; `rounded` frames
 * the grid with the card radius. Use `as` to render a `form`/`section`
 * instead of the default `div`.
 */
const HAIRLINE_GRID =
  "grid border-l border-t border-line [&>*]:border-b [&>*]:border-r [&>*]:border-line";
// The rounded frame (the v2 card radius, as Panel wears): the cells still draw
// the hairlines between them, but a straight cell border clipped by a radius
// stops short of the curve — so one rounded border is laid over the whole
// grid instead of the container's top + left rules, coinciding with the outer
// cell rules on the straight runs and carrying the corners on its own.
const HAIRLINE_GRID_ROUNDED =
  "relative grid overflow-hidden rounded-xl [&>*]:border-b [&>*]:border-r [&>*]:border-line " +
  "after:pointer-events-none after:absolute after:inset-0 after:rounded-xl after:border after:border-line";

export function HairlineGrid<T extends ElementType = "div">({
  as,
  rounded = false,
  className = "",
  ...props
}: { as?: T; rounded?: boolean; className?: string } & Omit<
  ComponentPropsWithoutRef<T>,
  "as" | "className"
>) {
  const Tag = (as ?? "div") as ElementType;
  return (
    <Tag
      className={`${rounded ? HAIRLINE_GRID_ROUNDED : HAIRLINE_GRID} ${className}`}
      {...props}
    />
  );
}

/**
 * A row of `StatCard`s: each stat is framed by its own rule, so the row needs
 * only columns and the gap that keeps neighbouring rules reading as separate
 * stats. Same API as `HairlineGrid` — pass the responsive column classes (and
 * any margin) via `className`, `as` to render a `section`.
 */
const STAT_ROW = "grid gap-x-8 gap-y-10";

export function StatRow<T extends ElementType = "div">({
  as,
  className = "",
  ...props
}: { as?: T; className?: string } & Omit<
  ComponentPropsWithoutRef<T>,
  "as" | "className"
>) {
  const Tag = (as ?? "div") as ElementType;
  return <Tag className={`${STAT_ROW} ${className}`} {...props} />;
}

/** Column template for a row of stat cards whose values can grow wide (money
 *  figures): as many columns as fit at ≥19rem each, the rest wrapping onto the
 *  next row — a long value gets a wider cell, the type size never changes.
 *  `min(…,100%)` keeps a single column working on the narrowest screens.
 *  Pass it as the StatRow className in place of fixed `grid-cols-*`. */
export const STAT_GRID_WRAP =
  "grid-cols-[repeat(auto-fit,minmax(min(19rem,100%),1fr))]";

export function StatCard({
  label,
  value,
  sub,
  href,
  variant,
  change,
}: {
  label: ReactNode;
  value: ReactNode;
  sub?: ReactNode;
  href?: string;
  variant?: "default" | "warn";
  /** Period-over-period movement. Only for stats whose prior window is
   *  exactly countable — see `statDelta`. */
  change?: StatChange;
}) {
  const delta = change ? statDelta(change.current, change.previous) : null;
  const inner = (
    <div className="relative min-h-32">
      {/* Each stat carries its own rule instead of sitting in a filled cell, so
          a row reads as a set of measurements rather than a set of boxes. */}
      <hr
        role="presentation"
        className="w-full border-t border-line transition-colors group-hover:border-fg-subtle"
      />
      {/* Linked cards get an open / go-to glyph (↗) so they read as navigable,
          vs static metric tiles. With no cell to fill on hover, the rule and
          the glyph are what brighten (group). */}
      {href && (
        <span
          aria-hidden
          className="absolute right-0 top-5 font-mono text-sm text-fg-subtle transition-colors group-hover:text-fg"
        >
          ↗
        </span>
      )}
      <p className="label mt-5">{label}</p>
      {/* Every stat hangs from its rule at the same offsets, so a row's numbers
          land on one line — a card carrying more underneath (a sub, a change)
          grows downward instead of floating its number up. */}
      <p
        className={`mt-4 font-display text-3xl tracking-tight sm:text-4xl ${
          variant === "warn" ? "text-amber-500" : "text-fg"
        }`}
      >
        {value}
      </p>
      {sub && <p className="mt-2 text-sm text-fg-muted">{sub}</p>}
      {change && delta && (
        <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1">
          <Badge variant={delta.variant}>{delta.text}</Badge>
          <span className="text-sm text-fg-muted">{change.window}</span>
        </p>
      )}
    </div>
  );
  return href ? (
    <Link href={href} className="group block">
      {inner}
    </Link>
  ) : (
    inner
  );
}

/**
 * A compact metric cell for list-page toolbars (a label + one big number),
 * sized to sit in a `HairlineGrid` toolbar beside the search cell. Distinct
 * from `StatCard`, the larger, optionally-linked dashboard tile. `warn` paints
 * the number amber; `muted` dims it (e.g. a zero count). Set `live` to announce
 * changes to assistive tech (a client-updated count).
 */
export function StatCell({
  label,
  value,
  variant = "default",
  live = false,
  corner,
}: {
  label: ReactNode;
  value: ReactNode;
  variant?: "default" | "warn" | "muted";
  live?: boolean;
  /** Optional marker pinned to the cell's upper-right corner (e.g. a status dot). */
  corner?: ReactNode;
}) {
  const color =
    variant === "warn"
      ? "text-amber-500"
      : variant === "muted"
        ? "text-fg-subtle"
        : "text-fg";
  return (
    <div
      className="relative flex flex-col justify-center bg-bg-muted p-4 sm:px-6"
      aria-live={live ? "polite" : undefined}
      aria-atomic={live ? true : undefined}
    >
      {corner != null && (
        <span className="absolute right-3 top-3">{corner}</span>
      )}
      <p className="label">{label}</p>
      <p className={`font-display text-3xl tracking-tight ${color}`}>{value}</p>
    </div>
  );
}
